/**
 * Resume Tuner Service
 *
 * Frontend service for resume customization
 * Handles API calls to Cloud Functions for CV fine-tuning
 */

import { getFunctions } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { getEmulatorHost, isUsingEmulators } from './emulator';
import {
  CustomizedResume,
  ConversationAchievement,
  JobRequirement,
  AchievementRelevance,
  ResumeTuningRequest,
  ResumeTuningResponse,
  SavedJobPosition,
} from '../types/resumeTuning';

function shouldForceTokenRefresh(): boolean {
  return isUsingEmulators();
}

function getFunctionsBaseUrl(functions: ReturnType<typeof getFunctions>): string {
  const projectId =
    functions.app.options.projectId ||
    process.env.REACT_APP_FIREBASE_PROJECT_ID ||
    'postmortem-pheonix';
  const region = functions.region || 'us-central1';

  if (isUsingEmulators()) {
    const host = getEmulatorHost();
    const port = process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5001';
    return `http://${host}:${port}/${projectId}/${region}`;
  }

  return functions.customDomain || `https://${region}-${projectId}.cloudfunctions.net`;
}

/**
 * Generate a customized resume for a specific job position
 */
export async function generateCustomizedResume(
  request: ResumeTuningRequest
): Promise<CustomizedResume> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Must be authenticated');
  }

  const idToken = await user.getIdToken(shouldForceTokenRefresh());
  const functions = getFunctions();
  const baseUrl = getFunctionsBaseUrl(functions);

  const response = await fetch(`${baseUrl}/generateCustomizedResume`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const result: ResumeTuningResponse = await response.json();

  if (!result.success) {
    throw new Error(result.error || 'Failed to generate customized resume');
  }

  return result.customizedResume!;
}

/**
 * Get recent achievements from user's conversations (last 12 weeks)
 */
export async function getRecentAchievementsFromConversations(): Promise<ConversationAchievement[]> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Must be authenticated');
  }

  const idToken = await user.getIdToken(shouldForceTokenRefresh());
  const functions = getFunctions();
  const baseUrl = getFunctionsBaseUrl(functions);

  const response = await fetch(`${baseUrl}/extractRecentAchievements`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  return data.achievements || [];
}

/**
 * Analyze job requirements from a job description
 */
export async function analyzeJobRequirements(
  jobDescription: string
): Promise<JobRequirement[]> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Must be authenticated');
  }

  const idToken = await user.getIdToken(shouldForceTokenRefresh());
  const functions = getFunctions();
  const baseUrl = getFunctionsBaseUrl(functions);

  const response = await fetch(`${baseUrl}/analyzeJobRequirements`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ description: jobDescription }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  return data.requirements || [];
}

/**
 * Score achievement relevance to job requirements
 */
export async function scoreAchievementRelevance(
  achievements: ConversationAchievement[],
  jobRequirements: JobRequirement[]
): Promise<AchievementRelevance[]> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Must be authenticated');
  }

  const idToken = await user.getIdToken(shouldForceTokenRefresh());
  const functions = getFunctions();
  const baseUrl = getFunctionsBaseUrl(functions);

  const response = await fetch(`${baseUrl}/scoreAchievementRelevance`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ achievements, requirements: jobRequirements }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  return data.scoredAchievements || [];
}

/**
 * Export customized resume to PDF or DOCX
 */
export async function exportCustomizedResume(
  customizedResumeId: string,
  format: 'pdf' | 'docx'
): Promise<Blob> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Must be authenticated');
  }

  const idToken = await user.getIdToken(shouldForceTokenRefresh());
  const functions = getFunctions();
  const baseUrl = getFunctionsBaseUrl(functions);

  const response = await fetch(`${baseUrl}/exportCustomizedResume`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ resumeId: customizedResumeId, format }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.blob();
}

/**
 * List all customized resumes for current user
 */
export async function listCustomizedResumes(): Promise<CustomizedResume[]> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Must be authenticated');
  }

  const idToken = await user.getIdToken(shouldForceTokenRefresh());
  const functions = getFunctions();
  const baseUrl = getFunctionsBaseUrl(functions);

  const response = await fetch(`${baseUrl}/listCustomizedResumes`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  return data.customizedResumes || [];
}

/**
 * Get a specific customized resume
 */
export async function getCustomizedResume(id: string): Promise<CustomizedResume> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Must be authenticated');
  }

  const idToken = await user.getIdToken(shouldForceTokenRefresh());
  const functions = getFunctions();
  const baseUrl = getFunctionsBaseUrl(functions);

  const response = await fetch(`${baseUrl}/getCustomizedResume?id=${id}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  return data.customizedResume;
}

/**
 * Delete a customized resume
 */
export async function deleteCustomizedResume(id: string): Promise<void> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Must be authenticated');
  }

  const idToken = await user.getIdToken(shouldForceTokenRefresh());
  const functions = getFunctions();
  const baseUrl = getFunctionsBaseUrl(functions);

  const response = await fetch(`${baseUrl}/deleteCustomizedResume`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ resumeId: id }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
}

/**
 * Save a job target for later resume generation
 */
export async function saveJobPosition(
  payload: Pick<SavedJobPosition, 'title' | 'company' | 'description'>
): Promise<SavedJobPosition> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Must be authenticated');
  }

  const idToken = await user.getIdToken(shouldForceTokenRefresh());
  const functions = getFunctions();
  const baseUrl = getFunctionsBaseUrl(functions);

  const response = await fetch(`${baseUrl}/saveJobPosition`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  return data.savedJob;
}

/**
 * List all saved jobs for current user
 */
export async function listSavedJobs(): Promise<SavedJobPosition[]> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Must be authenticated');
  }

  const idToken = await user.getIdToken(shouldForceTokenRefresh());
  const functions = getFunctions();
  const baseUrl = getFunctionsBaseUrl(functions);

  const response = await fetch(`${baseUrl}/listSavedJobs`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  return data.savedJobs || [];
}

/**
 * Tailor resume with LLM — uses career memory graph + Gemini/OpenAI to craft
 * a fully tailored resume with rewritten bullets and explicit skill bindings.
 */
export async function tailorResumeWithLLM(params: {
  jobTitle: string;
  company: string;
  jobDescription: string;
  tone?: 'technical' | 'executive' | 'balanced';
  userNotes?: string;
  savedJobId?: string;
  jobTargetId?: string;
  mode?: 'quick' | 'thorough';
  modelTier?: 'standard' | 'premium';
}): Promise<CustomizedResume> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Must be authenticated');
  }

  const idToken = await user.getIdToken(shouldForceTokenRefresh());
  const functions = getFunctions();
  const baseUrl = getFunctionsBaseUrl(functions);

  const response = await fetch(`${baseUrl}/tailorResumeWithLLM`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
  }

  const result = await response.json();
  if (!result.success) {
    throw new Error(result.error || 'Failed to tailor resume');
  }

  return result.customizedResume as CustomizedResume;
}

/**
 * Delete a saved job by id
 */
export async function deleteSavedJob(id: string): Promise<void> {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Must be authenticated');
  }

  const idToken = await user.getIdToken(shouldForceTokenRefresh());
  const functions = getFunctions();
  const baseUrl = getFunctionsBaseUrl(functions);

  const response = await fetch(`${baseUrl}/deleteSavedJob`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ savedJobId: id }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
}
